import type { BuilderState } from "../builder-state";
import {
  qIdent,
  qLiteral,
  NO_CAPS,
  type ConnectionString,
  type DialectCapabilities,
} from "./shared";

export const label = "PostgreSQL";
export const defaultPort = 5432;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  pgDatabaseOptions: true,
  pgRoleAttributes: true,
  schemaIsolation: true,
  sequences: true,
  schemaAndFunctions: true,
  defaultPrivileges: true,
  truncatePriv: true,
  referencesAndTrigger: true,
  wrapTransaction: true,
  quoteIdentifiers: true,
  pgUpdateHints: true,
  superuserToggle: true,
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (line = "") => out.push(line);
  const c = (t: string) => s.includeComments && push(`-- ${t}`);

  const db = qIdent(s.dbName, s.quoteIdentifiers);
  const user = qIdent(s.userName, s.quoteIdentifiers);
  const schema = qIdent(s.schemaName, s.quoteIdentifiers);
  const pwd = s.password ? qLiteral(s.password) : null;

  c("============================================================");
  c(`PostgreSQL — ${s.mode === "create" ? "Create" : "Update"} role & database`);
  c("Generated by DB User Forge. Review before running in production.");
  c("Run as a superuser (e.g. postgres) connected to the maintenance DB.");
  c("============================================================");
  push();

  if (s.wrapTransaction) {
    c("Transactions: CREATE DATABASE cannot run inside a transaction block.");
    c("The role + grants section is wrapped; database creation is left outside.");
    push();
  }

  // ---- ROLE ----
  if (s.mode === "create") {
    c("---- 1. ROLE -------------------------------------------------");
    push(`DO $$`);
    push(`BEGIN`);
    push(`  IF NOT EXISTS (SELECT 1 FROM pg_roles WHERE rolname = ${qLiteral(s.userName)}) THEN`);
    const attrs: string[] = [];
    attrs.push(s.canLogin ? "LOGIN" : "NOLOGIN");
    attrs.push(s.superuser ? "SUPERUSER" : "NOSUPERUSER");
    attrs.push(s.createDb ? "CREATEDB" : "NOCREATEDB");
    attrs.push(s.createRole ? "CREATEROLE" : "NOCREATEROLE");
    attrs.push(s.replication ? "REPLICATION" : "NOREPLICATION");
    attrs.push(s.bypassRls ? "BYPASSRLS" : "NOBYPASSRLS");
    attrs.push(s.inheritRole ? "INHERIT" : "NOINHERIT");
    if (s.connectionLimit !== -1) attrs.push(`CONNECTION LIMIT ${s.connectionLimit}`);
    if (pwd) attrs.push(`PASSWORD ${pwd}`);
    if (s.validUntil) attrs.push(`VALID UNTIL ${qLiteral(s.validUntil)}`);
    push(`    CREATE ROLE ${user} WITH ${attrs.join(" ")};`);
    push(`  END IF;`);
    push(`END$$;`);
    push();
  } else {
    c("---- 1. UPDATE ROLE -----------------------------------------");
    const alters: string[] = [];
    alters.push(s.canLogin ? "LOGIN" : "NOLOGIN");
    alters.push(s.superuser ? "SUPERUSER" : "NOSUPERUSER");
    alters.push(s.createDb ? "CREATEDB" : "NOCREATEDB");
    alters.push(s.createRole ? "CREATEROLE" : "NOCREATEROLE");
    alters.push(s.replication ? "REPLICATION" : "NOREPLICATION");
    alters.push(s.bypassRls ? "BYPASSRLS" : "NOBYPASSRLS");
    alters.push(s.inheritRole ? "INHERIT" : "NOINHERIT");
    if (s.connectionLimit !== -1) alters.push(`CONNECTION LIMIT ${s.connectionLimit}`);
    push(`ALTER ROLE ${user} WITH ${alters.join(" ")};`);
    if (s.resetPassword && pwd) push(`ALTER ROLE ${user} WITH PASSWORD ${pwd};`);
    if (s.validUntil) push(`ALTER ROLE ${user} VALID UNTIL ${qLiteral(s.validUntil)};`);
    push();
  }

  // ---- DATABASE ----
  if (s.mode === "create") {
    c("---- 2. DATABASE --------------------------------------------");
    c("CREATE DATABASE must run outside a transaction. Run this block first.");
    const owner = s.ownDatabase ? ` OWNER ${user}` : "";
    push(
      `CREATE DATABASE ${db}${owner} ENCODING ${qLiteral(s.encoding)} LC_COLLATE ${qLiteral(
        s.locale,
      )} LC_CTYPE ${qLiteral(s.locale)} TEMPLATE ${qIdent(s.template, s.quoteIdentifiers)};`,
    );
    push();
  }

  if (s.wrapTransaction) push("BEGIN;");
  push();

  // ---- CLUSTER ISOLATION ----
  if (s.revokeOtherDbConnect && s.mode === "create") {
    c("---- 3. CLUSTER ISOLATION -----------------------------------");
    c("Block PUBLIC from connecting to this database; only the owner can.");
    push(`REVOKE ALL ON DATABASE ${db} FROM PUBLIC;`);
    push(`GRANT CONNECT ON DATABASE ${db} TO ${user};`);
    if (s.hideOtherDatabases) {
      c("Note: hiding other databases from \\l requires REVOKE on pg_database");
      c("and is non-standard. Prefer pg_hba.conf to restrict per-db logins:");
      c(`  host  ${s.dbName}  ${s.userName}  0.0.0.0/0  scram-sha-256`);
      c(`  host  all  ${s.userName}  0.0.0.0/0  reject`);
    }
    push();
  }

  c(`---- 4. CONNECT TO TARGET DATABASE --------------------------`);
  c(`Run the following while connected to "${s.dbName}":`);
  push(`\\connect ${db}`);
  push();

  // ---- SCHEMA ----
  if (s.createSchema) {
    c("---- 5. SCHEMA ----------------------------------------------");
    push(`CREATE SCHEMA IF NOT EXISTS ${schema} AUTHORIZATION ${user};`);
    if (s.setSearchPath) {
      push(`ALTER ROLE ${user} IN DATABASE ${db} SET search_path TO ${schema}, public;`);
    }
    push();
  }

  if (s.revokePublicSchema) {
    c("---- 6. LOCK DOWN PUBLIC SCHEMA -----------------------------");
    c("PG15+ already restricts this. Re-asserting for older versions.");
    push(`REVOKE ALL ON SCHEMA public FROM PUBLIC;`);
    push(`REVOKE CREATE ON SCHEMA public FROM PUBLIC;`);
    push();
  }

  // ---- PRIVILEGES ----
  c("---- 7. PRIVILEGES ------------------------------------------");
  const targetSchemas = s.createSchema ? [schema] : [qIdent("public", s.quoteIdentifiers)];

  if (s.mode === "update" && s.revokeBeforeGrant) {
    c("Revoke existing grants before re-applying.");
    for (const sch of targetSchemas) {
      push(`REVOKE ALL ON ALL TABLES IN SCHEMA ${sch} FROM ${user};`);
      push(`REVOKE ALL ON ALL SEQUENCES IN SCHEMA ${sch} FROM ${user};`);
      push(`REVOKE ALL ON ALL FUNCTIONS IN SCHEMA ${sch} FROM ${user};`);
      push(`REVOKE ALL ON SCHEMA ${sch} FROM ${user};`);
    }
    push();
  }

  const tableP: string[] = [];
  if (s.privSelect) tableP.push("SELECT");
  if (s.privInsert) tableP.push("INSERT");
  if (s.privUpdate) tableP.push("UPDATE");
  if (s.privDelete) tableP.push("DELETE");
  if (s.privTruncate) tableP.push("TRUNCATE");
  if (s.privReferences) tableP.push("REFERENCES");
  if (s.privTrigger) tableP.push("TRIGGER");

  const seqP: string[] = [];
  if (s.privUsageSeq) seqP.push("USAGE");
  if (s.privSelectSeq) seqP.push("SELECT");
  if (s.privUpdateSeq) seqP.push("UPDATE");

  for (const sch of targetSchemas) {
    const schemaPrivs: string[] = [];
    if (s.privUsageSchema) schemaPrivs.push("USAGE");
    if (s.privCreateSchema) schemaPrivs.push("CREATE");
    if (schemaPrivs.length) push(`GRANT ${schemaPrivs.join(", ")} ON SCHEMA ${sch} TO ${user};`);

    if (tableP.length)
      push(`GRANT ${tableP.join(", ")} ON ALL TABLES IN SCHEMA ${sch} TO ${user};`);
    if (seqP.length) push(`GRANT ${seqP.join(", ")} ON ALL SEQUENCES IN SCHEMA ${sch} TO ${user};`);
    if (s.privExecuteFn) push(`GRANT EXECUTE ON ALL FUNCTIONS IN SCHEMA ${sch} TO ${user};`);
  }
  push();

  // ---- DEFAULT PRIVILEGES ----
  if (s.defaultPrivileges) {
    c("---- 8. DEFAULT PRIVILEGES (for future objects) -------------");
    const forRole = s.defaultPrivilegesFor.trim()
      ? `FOR ROLE ${qIdent(s.defaultPrivilegesFor.trim(), s.quoteIdentifiers)} `
      : "";
    for (const sch of targetSchemas) {
      if (tableP.length)
        push(
          `ALTER DEFAULT PRIVILEGES ${forRole}IN SCHEMA ${sch} GRANT ${tableP.join(
            ", ",
          )} ON TABLES TO ${user};`,
        );
      if (seqP.length)
        push(
          `ALTER DEFAULT PRIVILEGES ${forRole}IN SCHEMA ${sch} GRANT ${seqP.join(
            ", ",
          )} ON SEQUENCES TO ${user};`,
        );
      if (s.privExecuteFn)
        push(
          `ALTER DEFAULT PRIVILEGES ${forRole}IN SCHEMA ${sch} GRANT EXECUTE ON FUNCTIONS TO ${user};`,
        );
    }
    push();
  }

  if (s.mode === "update" && s.dropOwnedReassign) {
    c("---- 9. RESET OWNERSHIP -------------------------------------");
    push(`REASSIGN OWNED BY ${user} TO CURRENT_USER;`);
    push(
      `-- DROP OWNED BY ${qIdent(s.userName, s.quoteIdentifiers)}; -- destructive, review first`,
    );
    push();
  }

  if (s.wrapTransaction) push("COMMIT;");
  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const dbSeg = encodeURIComponent(s.dbName);
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    { label: "libpq / psql", url: `postgresql://${auth}@${s.host}:${s.port}/${dbSeg}` },
    {
      label: "JDBC",
      url: `jdbc:postgresql://${s.host}:${s.port}/${dbSeg}?user=${user}&password=${pwd}`,
    },
    {
      label: "SQLAlchemy / Django",
      url: `postgresql://${auth}@${s.host}:${s.port}/${dbSeg}`,
    },
    { label: "Node.js (pg)", url: `postgres://${auth}@${s.host}:${s.port}/${dbSeg}` },
  ];
}
